// src/pages/TenantSetupPage.js

import React, { useState, useEffect } from 'react';
import ManualTenantWizard from '../components/ManualTenantWizard';

const TenantSetupPage = ({ currentUser }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showManualWizard, setShowManualWizard] = useState(false);
  const [myCreatedTenants, setMyCreatedTenants] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);

  // Load the user's tenant creation history
  useEffect(() => {
    const loadTenantHistory = async () => {
      if (!currentUser?.uid) return;
      
      try {
        setIsLoadingHistory(true);
        
        // Query tenants created by this user
        const { collection, query, where, getDocs, getFirestore, orderBy } = await import('firebase/firestore');
        const db = getFirestore();
        
        const tenantsQuery = query(
          collection(db, 'tenants'),
          where('createdBy', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await getDocs(tenantsQuery);
        const tenants = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date()
        }));
        
        setMyCreatedTenants(tenants);
      } catch (error) {
        console.error('Error loading tenant history:', error);
        // Don't show error to user - this is just for nice-to-have history
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadTenantHistory();
  }, [currentUser?.uid, successMessage]); // Reload when a new tenant is created

  // Handle manual wizard submission
  const handleManualSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      setErrorMessage('');
      
      console.log('Creating tenant:', formData);
      
      // Add the createdBy field to track who created this tenant
      const tenantData = {
        ...formData,
        createdBy: currentUser.uid,
        createdByEmail: currentUser.email,
        createdByRole: 'Tenant Setup Admin',
        setupSource: 'manual_wizard'
      };
      
      const response = await fetch('https://us-east1-relief-roster-43593.cloudfunctions.net/createTenantDirectly', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(tenantData)
      });

      const result = await response.json();

      if (result.success) {
        setSuccessMessage(`🎉 School setup completed successfully!\n\n` +
          `School: ${result.schoolName}\n` +
          `URL: ${result.url}\n` +
          `Tenant ID: ${result.tenantId}\n\n` +
          `The school's Relief Roster is now being configured and will be ready shortly.`);
        
        // Close the modal
        setShowManualWizard(false);
        
        // Clear success message after 10 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 10000);
        
      } else {
        throw new Error(result.error || 'Failed to create tenant');
      }

    } catch (error) {
      console.error('Error creating tenant:', error);
      setErrorMessage(`❌ Error: ${error.message}`);
      
      // Clear error message after 8 seconds
      setTimeout(() => {
        setErrorMessage('');
      }, 8000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const userDisplayName = currentUser?.displayName || currentUser?.email || 'Tenant Setup Admin';

  return (
    <div style={{ maxWidth: '1000px', margin: '20px auto', fontFamily: 'sans-serif', padding: '0 20px' }}>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '30px',
        borderRadius: '10px',
        marginBottom: '30px',
        textAlign: 'center'
      }}>
        <h1 style={{ margin: '0 0 10px 0', fontSize: '2.5em' }}>🏫 School Setup Wizard</h1>
        <p style={{ margin: 0, fontSize: '1.1em', opacity: 0.9 }}>
          Welcome, {userDisplayName}! Use the manual setup wizard to create new school tenants.
        </p>
      </div>
      
      {/* Global Success/Error Messages */}
      {successMessage && (
        <div style={{ 
          padding: '20px', 
          backgroundColor: '#d4edda', 
          border: '1px solid #c3e6cb',
          borderRadius: '8px',
          color: '#155724',
          marginBottom: '25px',
          fontSize: '14px',
          whiteSpace: 'pre-line',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div style={{ 
          padding: '20px', 
          backgroundColor: '#f8d7da', 
          border: '1px solid #f5c6cb',
          borderRadius: '8px',
          color: '#721c24',
          marginBottom: '25px',
          fontSize: '14px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          {errorMessage}
        </div>
      )}

      {/* Main Setup Section */}
      <div style={{ 
        padding: '30px', 
        border: '2px solid #6f42c1', 
        borderRadius: '12px', 
        backgroundColor: '#faf9ff',
        marginBottom: '30px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '25px' }}>
          <h2 style={{ color: '#6f42c1', marginTop: 0, fontSize: '1.8em' }}>
            📝 Manual School Setup
          </h2>
          <p style={{ fontSize: '16px', color: '#666', margin: '0 auto', maxWidth: '600px', lineHeight: '1.5' }}>
            Create a complete Relief Roster setup for a school by filling out their information using our comprehensive 6-step wizard. 
            This process will create the school's tenant, configure their settings, and prepare their system for use.
          </p>
        </div>

        <div style={{ 
          background: '#e8f4f8', 
          padding: '20px', 
          borderRadius: '8px', 
          marginBottom: '25px',
          border: '1px solid #bee5eb'
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#0c5460' }}>✨ What the wizard includes:</h4>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '10px',
            fontSize: '14px',
            color: '#0c5460'
          }}>
            <div>• School information & branding</div>
            <div>• Contact details & preferences</div>
            <div>• Department & staff setup</div>
            <div>• System configuration</div>
            <div>• Integration preferences</div>
            <div>• Custom groups & permissions</div>
          </div>
        </div>

        <div style={{ textAlign: 'center' }}>
          <button
            onClick={() => setShowManualWizard(true)}
            disabled={isSubmitting}
            style={{
              padding: '15px 30px',
              backgroundColor: isSubmitting ? '#6c757d' : '#6f42c1',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 'bold',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              transition: 'all 0.2s',
              boxShadow: '0 3px 6px rgba(111, 66, 193, 0.3)',
              transform: isSubmitting ? 'none' : 'translateY(-1px)'
            }}
            onMouseOver={(e) => {
              if (!isSubmitting) {
                e.target.style.backgroundColor = '#5a2d91';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 8px rgba(111, 66, 193, 0.4)';
              }
            }}
            onMouseOut={(e) => {
              if (!isSubmitting) {
                e.target.style.backgroundColor = '#6f42c1';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 3px 6px rgba(111, 66, 193, 0.3)';
              }
            }}
          >
            {isSubmitting ? '⏳ Creating School...' : '🚀 Start New School Setup'}
          </button>
        </div>
      </div>

      {/* My Created Schools History */}
      <div style={{ 
        padding: '25px', 
        border: '1px solid #dee2e6', 
        borderRadius: '8px', 
        backgroundColor: '#f8f9fa'
      }}>
        <h3 style={{ margin: '0 0 20px 0', color: '#495057', display: 'flex', alignItems: 'center', gap: '10px' }}>
          📚 My Created Schools
          {isLoadingHistory && <span style={{ fontSize: '14px', color: '#6c757d' }}>Loading...</span>}
        </h3>
        
        {!isLoadingHistory && myCreatedTenants.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px', 
            color: '#6c757d',
            fontStyle: 'italic'
          }}>
            <p style={{ margin: 0, fontSize: '16px' }}>
              🌟 No schools created yet. Start by using the setup wizard above!
            </p>
          </div>
        )}

        {!isLoadingHistory && myCreatedTenants.length > 0 && (
          <div style={{ 
            display: 'grid', 
            gap: '15px',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))'
          }}>
            {myCreatedTenants.map((tenant) => (
              <div 
                key={tenant.id} 
                style={{ 
                  padding: '15px', 
                  border: '1px solid #e9ecef', 
                  borderRadius: '6px', 
                  backgroundColor: 'white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
              >
                <h4 style={{ margin: '0 0 8px 0', color: '#343a40', fontSize: '16px' }}>
                  🏫 {tenant.schoolName || 'Unknown School'}
                </h4>
                <p style={{ margin: '5px 0', fontSize: '14px', color: '#6c757d' }}>
                  <strong>URL:</strong> <a 
                    href={`https://${tenant.subdomain}.reliefroster.com`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#007bff', textDecoration: 'none' }}
                  >
                    {tenant.subdomain}.reliefroster.com
                  </a>
                </p>
                <p style={{ margin: '5px 0', fontSize: '14px', color: '#6c757d' }}>
                  <strong>Contact:</strong> {tenant.contactPersonName || tenant.primaryAdminEmail || 'N/A'}
                </p>
                <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#868e96' }}>
                  Created: {formatDate(tenant.createdAt)}
                </p>
              </div>
            ))}
          </div>
        )}

        {!isLoadingHistory && myCreatedTenants.length > 0 && (
          <div style={{ 
            marginTop: '15px', 
            padding: '10px', 
            backgroundColor: '#e2f3ff', 
            borderRadius: '4px', 
            fontSize: '12px', 
            color: '#0056b3',
            textAlign: 'center'
          }}>
            ✅ You have successfully created {myCreatedTenants.length} school{myCreatedTenants.length === 1 ? '' : 's'} using the setup wizard.
          </div>
        )}
      </div>

      {/* Manual Wizard Modal */}
      {showManualWizard && (
        <ManualTenantWizard 
          onClose={() => setShowManualWizard(false)}
          onSubmit={handleManualSubmit}
        />
      )}
    </div>
  );
};

export default TenantSetupPage;